/**
 * Header and Footer editing.
 *
 * Both are global site chrome rather than page content, so they get their own
 * panel modes instead of appearing among a page's sections. Controls are
 * bounded choices mapped onto the canonical schema -- there is no field here
 * that accepts CSS, HTML or a free-form colour.
 *
 * Footer editing is deliberately contextual: choosing a column and then an
 * element shows only that element's fields, instead of one page-long form
 * containing every property of every possible element.
 */
import React, { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';

import { SOCIAL_PLATFORMS, SocialMark } from '../render/CanvasChrome';
import { configuredFooterMinimumHeight, geometryForViewport } from '../freeformFooter';
import { createBlockId } from '../ids';
import type { FreeformGeometry, MediaTarget, SiteChrome, SitePage, ViewportKey } from '../types';

const field =
  'mt-1.5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20';

type Footer = SiteChrome['footer'];
type FooterColumn = NonNullable<Footer['columns']>[number];
type FooterElement = FooterColumn['elements'][number];
const EMPTY_FOOTER_COLUMNS: FooterColumn[] = [];

const FOOTER_ELEMENT_LABELS: Record<FooterElement['type'], string> = {
  logo: 'Logo',
  text: 'Tekst',
  links: 'Navigatie',
  contact: 'Contactgegevens',
  button: 'Knop',
  socials: 'Social media',
  partner_logos: "Partnerlogo's",
  legal: 'Juridische links',
  copyright: 'Copyright',
  cookie_preferences: 'Cookievoorkeuren',
  spacer: 'Ruimte',
  divider: 'Scheiding',
  shape: 'Vorm',
};

const FOOTER_TEMPLATES: Array<{ id: string; label: string; description: string; types: FooterElement['type'][][] }> = [
  { id: 'minimal', label: 'Minimal', description: 'Eén rustige kolom met merk en tekst.', types: [['logo', 'text', 'copyright', 'cookie_preferences', 'legal', 'links', 'contact', 'socials', 'partner_logos', 'button']] },
  { id: 'standard', label: 'Standaard', description: 'Merk, navigatie en contact.', types: [['logo', 'text'], ['links', 'legal', 'cookie_preferences'], ['contact', 'socials', 'partner_logos', 'button', 'copyright']] },
  { id: 'wide', label: 'Breed met navigatie', description: 'Vier duidelijke informatiekolommen.', types: [['logo', 'text'], ['links', 'legal'], ['contact', 'button'], ['socials', 'partner_logos', 'copyright', 'cookie_preferences']] },
  { id: 'compact', label: 'Compact', description: 'Merk, navigatie en contact naast elkaar.', types: [['logo', 'text'], ['links', 'legal', 'cookie_preferences'], ['contact', 'socials', 'partner_logos', 'button', 'copyright']] },
];

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export function HeaderInspector({
  site,
  pages,
  onUpdateHeader,
  onUpdateSettings,
  onOpenMedia,
}: {
  site: SiteChrome;
  pages: SitePage[];
  onUpdateHeader: (patch: Partial<SiteChrome['header']>) => void;
  onUpdateSettings: (patch: Partial<SiteChrome['settings']>) => void;
  onOpenMedia: (target: MediaTarget) => void;
}) {
  const header = site.header;
  const cta = header.cta ?? null;

  return (
    <div className="space-y-6">
      <Intro
        title="Header"
        description="De balk bovenaan elke pagina. Deze instellingen gelden voor de hele website."
      />

      <PickerGroup
        label="Indeling"
        value={header.layout}
        onChange={(value) => onUpdateHeader({ layout: value as SiteChrome['header']['layout'] })}
        options={[
          { value: 'standard', label: 'Logo links, navigatie rechts', preview: <HeaderMini variant="standard" /> },
          { value: 'split', label: 'Navigatie gecentreerd', preview: <HeaderMini variant="split" /> },
          { value: 'centered', label: 'Logo gecentreerd', preview: <HeaderMini variant="centered" /> },
        ]}
      />

      <Segmented
        label="Gedrag bij scrollen"
        value={header.behavior}
        options={[
          ['static', 'Normaal'],
          ['sticky', 'Blijft staan'],
          ['transparent', 'Transparant'],
          ['transparent-sticky', 'Transparant + sticky'],
        ]}
        onChange={(value) => onUpdateHeader({ behavior: value as SiteChrome['header']['behavior'] })}
      />
      {header.behavior === 'transparent' ? (
        // Said here rather than discovered later: the renderer refuses a
        // transparent header over a light section, because white navigation
        // on a white hero is unreadable.
        <Note>
          Transparant werkt alleen boven een donkere hero. Staat er een lichte sectie bovenaan,
          dan toont de website automatisch een gewone header.
        </Note>
      ) : null}
      {header.behavior === 'transparent-sticky' ? (
        <div className="space-y-3 rounded-xl border border-teal-100 bg-teal-50/60 p-3">
          <p className="text-xs leading-relaxed text-teal-900">Boven de hero is de header transparant. Na scrollen krijgt hij automatisch een eigen leesbare surface.</p>
          <ColorField label="Achtergrond na scrollen" value={header.stickyBackgroundColor ?? ''} placeholder="Thema-achtergrond" onChange={(value) => onUpdateHeader({ stickyBackgroundColor: value || null })} />
          <ColorField label="Tekst na scrollen" value={header.stickyTextColor ?? ''} placeholder="Thema-tekst" onChange={(value) => onUpdateHeader({ stickyTextColor: value || null })} />
          <Segmented label="Schaduw na scrollen" value={header.stickyShadow ?? 'subtle'} options={[['none', 'Geen'], ['subtle', 'Subtiel'], ['strong', 'Sterk']]} onChange={(value) => onUpdateHeader({ stickyShadow: value as NonNullable<SiteChrome['header']['stickyShadow']> })} />
        </div>
      ) : null}

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Mobiele navigatie</h4>
        <Segmented label="Menuweergave" value={header.mobileMenuStyle ?? 'dropdown'} options={[['dropdown', 'Uitklappen'], ['offcanvas', 'Zijpaneel'], ['fullscreen', 'Volledig scherm']]} onChange={(value) => onUpdateHeader({ mobileMenuStyle: value as NonNullable<SiteChrome['header']['mobileMenuStyle']> })} />
        <Segmented label="Menuknop" value={header.mobileMenuButton ?? 'lines'} options={[['lines', 'Lijnen'], ['dots', 'Punten'], ['label', 'Menu-label']]} onChange={(value) => onUpdateHeader({ mobileMenuButton: value as NonNullable<SiteChrome['header']['mobileMenuButton']> })} />
        <Note>Op mobiel opent het menu met de toetsenbordfocus, sluit Escape het menu en wordt scrollen van de pagina tijdelijk geblokkeerd bij zijpaneel en volledig scherm.</Note>
      </div>

      <Segmented
        label="Achtergrond"
        value={header.surface}
        options={[['light', 'Licht'], ['dark', 'Donker'], ['theme', 'Thema']]}
        onChange={(value) => onUpdateHeader({ surface: value as SiteChrome['header']['surface'] })}
      />
      <ColorField label="Achtergrondkleur" value={header.backgroundColor ?? ''} placeholder="Automatisch vanuit bovenstaande keuze" onChange={(value) => onUpdateHeader({ backgroundColor: value || null })} />
      <ColorField label="Tekstkleur" value={header.textColor ?? ''} placeholder="Automatisch vanuit bovenstaande keuze" onChange={(value) => onUpdateHeader({ textColor: value || null })} />

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Logo en naam</h4>
        <div className="block">
          <span className="block text-sm font-semibold text-slate-800">Websitenaam</span>
          <input
            value={site.settings.siteName}
            onChange={(event) => onUpdateSettings({ siteName: event.target.value })}
            placeholder="Kerk aan de Vaart"
            className={field}
          />
        </div>
        <Toggle
          label="Naam tonen naast het logo"
          checked={header.showSiteName}
          onChange={(checked) => onUpdateHeader({ showSiteName: checked })}
        />
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3">
          <span className="block text-sm font-semibold text-slate-800">Logo</span>
          <button type="button" onClick={() => onOpenMedia({ kind: 'header-logo' })} className="mt-2 w-full rounded-lg border border-dashed border-teal-300 bg-teal-50 px-3 py-2.5 text-left text-xs font-semibold text-teal-800 hover:bg-teal-100">
            {header.logoAssetId ? 'Logo wijzigen' : 'Logo uploaden of kiezen'}
          </button>
          <span className="mt-1.5 block text-xs text-slate-500">Opent direct de mediabibliotheek.</span>
        </div>
      </div>

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Knop in de header</h4>
        <Toggle
          label="Knop tonen"
          checked={Boolean(cta)}
          onChange={(checked) => onUpdateHeader({ cta: checked ? { label: 'Plan je bezoek', pageId: null, href: null } : null })}
        />
        {cta ? (
          <>
            <label className="block">
              <span className="block text-sm font-semibold text-slate-800">Tekst</span>
              <input
                value={cta.label}
                onChange={(event) => onUpdateHeader({ cta: { ...cta, label: event.target.value } })}
                className={field}
              />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-slate-800">Gaat naar</span>
              <select
                value={cta.pageId ?? ''}
                onChange={(event) => onUpdateHeader({ cta: { ...cta, pageId: event.target.value || null, href: null } })}
                className={field}
              >
                <option value="">Kies een pagina…</option>
                {pages.map((page) => (
                  <option key={page.pageId} value={page.pageId}>{page.title || 'Naamloze pagina'}</option>
                ))}
              </select>
            </label>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** Tiny wireframe of a header layout. Same purpose as the layout chooser's
 * miniatures: the shape is the label. */
function HeaderMini({ variant }: { variant: 'standard' | 'split' | 'centered' }) {
  const bar = 'h-1.5 rounded-full bg-slate-300';
  if (variant === 'centered') {
    return (
      <span aria-hidden="true" className="flex h-9 w-full flex-col items-center justify-center gap-1.5 rounded-lg bg-white px-2 ring-1 ring-inset ring-slate-200">
        <span className={cn(bar, 'w-8 bg-slate-400')} />
        <span className="flex gap-1"><span className={cn(bar, 'w-4')} /><span className={cn(bar, 'w-4')} /><span className={cn(bar, 'w-4')} /></span>
      </span>
    );
  }
  return (
    <span aria-hidden="true" className="flex h-9 w-full items-center gap-1.5 rounded-lg bg-white px-2 ring-1 ring-inset ring-slate-200">
      <span className={cn(bar, 'w-6 bg-slate-400')} />
      <span className={cn('flex gap-1', variant === 'split' ? 'mx-auto' : 'ml-auto')}>
        <span className={cn(bar, 'w-3.5')} /><span className={cn(bar, 'w-3.5')} /><span className={cn(bar, 'w-3.5')} />
      </span>
      {variant === 'standard' ? <span className="h-3 w-6 rounded bg-teal-300" /> : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

export function FooterInspector({
  site,
  pages,
  onUpdateFooter,
  selectedElementId,
  onOpenMedia,
}: {
  site: SiteChrome;
  pages: SitePage[];
  onUpdateFooter: (patch: Partial<Footer>) => void;
  selectedElementId?: string | null;
  onOpenMedia: (target: MediaTarget) => void;
}) {
  const footer = site.footer;
  const columns = footer.columns ?? EMPTY_FOOTER_COLUMNS;
  const copyrightElement = columns.flatMap((column) => column.elements).find((element) => element.type === 'copyright');
  const [selected, setSelected] = useState<{ columnId: string; elementId: string } | null>(null);
  useEffect(() => {
    if (!selectedElementId) return;
    const column = columns.find((item) => item.elements.some((element) => element.elementId === selectedElementId));
    if (column) setSelected({ columnId: column.columnId, elementId: selectedElementId });
  }, [columns, selectedElementId]);

  const setColumns = (next: FooterColumn[]) =>
    onUpdateFooter({ columns: next, columnLayout: String(Math.max(1, next.length)) as Footer['columnLayout'] });

  const applyTemplate = (template: typeof FOOTER_TEMPLATES[number]) => {
    const existing = columns.flatMap((column) => column.elements);
    const next = template.types.map((types) => ({
      columnId: createBlockId(),
      elements: existing.filter((element) => types.includes(element.type)),
    }));
    next.forEach((column, index) => {
      if (column.elements.length === 0) column.elements.push(newFooterElement(template.types[index]![0]!));
    });
    setColumns(next);
  };

  const changeLayout = (count: number) => {
    const existingElements = columns.flatMap((column) => column.elements);
    if (count > columns.length && existingElements.length > 0) {
      setColumns(Array.from({ length: count }, (_, index) => ({
        columnId: columns[index]?.columnId ?? createBlockId(),
        elements: existingElements.filter((_, elementIndex) => elementIndex % count === index),
      })));
      return;
    }
    const next = Array.from({ length: count }, (_, index) => columns[index] ?? { columnId: createBlockId(), elements: [] });
    // Shrinking never discards content: everything from the removed columns
    // moves into the last surviving one, in order, so the owner can drag it
    // back rather than losing it.
    const orphaned = columns.slice(count).flatMap((column) => column.elements);
    if (orphaned.length) {
      const last = next[count - 1]!;
      next[count - 1] = { ...last, elements: [...last.elements, ...orphaned] };
    }
    setColumns(next);
  };

  const distributeElements = () => {
    const standard = FOOTER_TEMPLATES.find((template) => template.id === 'standard');
    if (standard) applyTemplate(standard);
  };

  const addElement = (columnId: string, type: FooterElement['type']) => {
    setColumns(columns.map((column) => column.columnId !== columnId ? column : {
      ...column,
      elements: [...column.elements, newFooterElement(type)],
    }));
  };

  const addCopyrightElement = () => {
    const nextColumns = columns.length
      ? columns.map((column) => ({ ...column, elements: [...column.elements] }))
      : [{
          columnId: createBlockId(),
          elements: [
            ...(footer.showSiteName !== false ? [newFooterElement('logo', { heading: site.settings.siteName || 'Onze gemeente' })] : []),
            ...(footer.description ? [newFooterElement('text', { text: footer.description })] : []),
          ],
        }];
    const last = nextColumns[nextColumns.length - 1]!;
    const copyright = newFooterElement('copyright', {
      elementId: 'footer-copyright',
      text: footer.smallText || `© ${new Date().getFullYear()} ${site.settings.siteName || 'Onze gemeente'}`,
      fontSize: footer.smallTextFontSize,
      align: footer.smallTextAlign,
    });
    last.elements.push(copyright);
    setColumns(nextColumns);
    onUpdateFooter({ smallText: copyright.text, showSmallText: true });
    setSelected({ columnId: last.columnId, elementId: copyright.elementId });
  };

  const patchElement = (columnId: string, elementId: string, patch: Partial<FooterElement>) => {
    setColumns(columns.map((column) => column.columnId !== columnId ? column : {
      ...column,
      elements: column.elements.map((element) => element.elementId === elementId ? { ...element, ...patch } : element),
    }));
  };

  const removeElement = (columnId: string, elementId: string) => {
    const removed = columns.find((column) => column.columnId === columnId)?.elements.find((element) => element.elementId === elementId);
    setColumns(columns.map((column) => column.columnId !== columnId ? column : {
      ...column,
      elements: column.elements.filter((element) => element.elementId !== elementId),
    }));
    if (removed?.type === 'copyright') onUpdateFooter({ showSmallText: false, smallText: '' });
    setSelected(null);
  };

  const moveElement = (columnId: string, elementId: string, delta: number) => {
    setColumns(columns.map((column) => {
      if (column.columnId !== columnId) return column;
      const index = column.elements.findIndex((element) => element.elementId === elementId);
      const target = index + delta;
      if (index === -1 || target < 0 || target >= column.elements.length) return column;
      const elements = column.elements.slice();
      const [moved] = elements.splice(index, 1);
      elements.splice(target, 0, moved!);
      return { ...column, elements };
    }));
  };

  const moveToColumn = (fromColumnId: string, elementId: string, toColumnId: string) => {
    const element = columns.find((c) => c.columnId === fromColumnId)?.elements.find((e) => e.elementId === elementId);
    if (!element) return;
    setColumns(columns.map((column) => {
      if (column.columnId === fromColumnId) {
        return { ...column, elements: column.elements.filter((e) => e.elementId !== elementId) };
      }
      if (column.columnId === toColumnId) return { ...column, elements: [...column.elements, element] };
      return column;
    }));
    setSelected({ columnId: toColumnId, elementId });
  };

  const selectedElement = selected
    ? columns.find((c) => c.columnId === selected.columnId)?.elements.find((e) => e.elementId === selected.elementId) ?? null
    : null;

  return (
    <div className="space-y-6">
      <Intro title="Footer" description="Onderaan elke pagina. Kies kolommen en vul ze met onderdelen." />

      <div className="space-y-2">
        <h4 className="text-sm font-bold text-slate-900">Footer-template</h4>
        <p className="text-xs text-slate-500">Kies een professionele basisindeling. Je bestaande inhoud blijft behouden.</p>
        <div className="grid grid-cols-2 gap-2">
          {FOOTER_TEMPLATES.map((template) => (
            <button key={template.id} type="button" onClick={() => applyTemplate(template)} className="rounded-xl border border-slate-200 p-2.5 text-left transition hover:border-teal-300 hover:bg-teal-50/50">
              <span className="block text-sm font-semibold text-slate-800">{template.label}</span>
              <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">{template.description}</span>
            </button>
          ))}
        </div>
      </div>

      <PickerGroup
        label="Kolommen"
        value={String(Math.max(columns.length, 1))}
        onChange={(value) => changeLayout(Number(value))}
        columns={4}
        options={[1, 2, 3, 4].map((count) => ({
          value: String(count),
          label: `${count} ${count === 1 ? 'kolom' : 'kolommen'}`,
          preview: (
            <span aria-hidden="true" className="flex h-8 w-full gap-1 rounded-lg bg-white p-1 ring-1 ring-inset ring-slate-200">
              {Array.from({ length: count }, (_, index) => <span key={index} className="flex-1 rounded-sm bg-slate-200" />)}
            </span>
          ),
        }))}
      />
      <Segmented
        label="Plaatsing"
        value={footer.positioning ?? 'flow'}
        options={[['flow', 'Automatisch'], ['freeform', 'Vrij plaatsen']]}
        onChange={(value) => onUpdateFooter({ positioning: value as Footer['positioning'] })}
      />
      {footer.positioning === 'freeform' ? <p className="-mt-3 text-xs text-slate-500">Sleep onderdelen rechtstreeks over het vrije vlak. Het raster is alleen een uitlijnhulp.</p> : null}
      <Toggle label="Secties en footer op elkaar aansluiten" checked={footer.connectToLastSection === true} onChange={(checked) => onUpdateFooter({ connectToLastSection: checked })} />
      {footer.connectToLastSection ? <p className="-mt-1 text-xs text-slate-500">Verwijdert de extra ruimte tussen secties en laat de footer direct aansluiten. Sectie toevoegen blijft beschikbaar.</p> : null}
      {columns.length > 1 && columns.some((column) => column.elements.length > 0) ? (
        <button type="button" onClick={distributeElements} className="w-full rounded-xl border border-teal-200 bg-teal-50 px-3 py-2.5 text-sm font-semibold text-teal-800 transition hover:border-teal-400 hover:bg-teal-100">
          Nette indeling toepassen
        </button>
      ) : null}

      <label className="block text-sm font-semibold text-slate-800">Hoogte footer (px)
        <input type="number" min={160} max={1000} step={1} value={footer.height ?? ''} onChange={(event) => onUpdateFooter({ height: event.target.value ? configuredFooterMinimumHeight(Number(event.target.value)) : undefined })} placeholder="Automatisch" className={field} />
      </label>

      <Segmented
        label="Achtergrond"
        value={footer.surface}
        options={[['dark', 'Donker'], ['light', 'Licht'], ['theme', 'Thema']]}
        onChange={(value) => onUpdateFooter({ surface: value as Footer['surface'] })}
      />
      <ColorField label="Achtergrondkleur" value={footer.backgroundColor ?? ''} placeholder="Automatisch vanuit bovenstaande keuze" onChange={(value) => onUpdateFooter({ backgroundColor: value || null })} />
      <ColorField label="Tekstkleur" value={footer.textColor ?? ''} placeholder="Automatisch vanuit bovenstaande keuze" onChange={(value) => onUpdateFooter({ textColor: value || null })} />

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Footerteksten</h4>
        <Toggle label="Naam van de website tonen" checked={footer.showSiteName !== false} onChange={(checked) => onUpdateFooter({ showSiteName: checked })} />
        <Toggle label="Copyrightregel tonen" checked={footer.showSmallText !== false} onChange={(checked) => onUpdateFooter({ showSmallText: checked })} />
        <div className="block">
          <span className="block text-sm font-semibold text-slate-800">Beschrijving</span>
          <textarea value={footer.description} onChange={(event) => onUpdateFooter({ description: event.target.value })} rows={4} className={`${field} resize-y`} placeholder="Korte tekst over je organisatie" />
          <label className="mt-2 block text-xs font-semibold text-slate-700">Tekstgrootte (px)
            <input type="number" min={10} max={72} value={footer.descriptionFontSize ?? ''} onChange={(event) => onUpdateFooter({ descriptionFontSize: event.target.value ? Number(event.target.value) : undefined })} placeholder="Site standaard" className={field} />
          </label>
          <Segmented label="Uitlijning" value={footer.descriptionAlign ?? 'left'} options={[['left', 'Links'], ['center', 'Midden'], ['right', 'Rechts']]} onChange={(value) => onUpdateFooter({ descriptionAlign: value as Footer['descriptionAlign'] })} />
        </div>
        <div className="block">
          <span className="block text-sm font-semibold text-slate-800">Onderste regel</span>
          <input value={copyrightElement?.text ?? footer.smallText} onChange={(event) => onUpdateFooter({ smallText: event.target.value, showSmallText: true })} className={field} placeholder={`© ${new Date().getFullYear()} ${site.settings.siteName || 'Organisatienaam'}`} />
          <label className="mt-2 block text-xs font-semibold text-slate-700">Tekstgrootte (px)
            <input type="number" min={10} max={40} value={footer.smallTextFontSize ?? ''} onChange={(event) => onUpdateFooter({ smallTextFontSize: event.target.value ? Number(event.target.value) : undefined })} placeholder="Site standaard" className={field} />
          </label>
          <Segmented label="Uitlijning" value={footer.smallTextAlign ?? 'left'} options={[['left', 'Links'], ['center', 'Midden'], ['right', 'Rechts']]} onChange={(value) => onUpdateFooter({ smallTextAlign: value as Footer['smallTextAlign'] })} />
        </div>
        <p className="text-xs text-slate-500">Klik de copyrightregel in het canvas om hem als normaal footeronderdeel te selecteren. Kies daarna <strong>Vrij plaatsen</strong> om hem te slepen, breder te maken of te verplaatsen.</p>
      </div>

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Onderdelen</h4>
        {!copyrightElement ? <button type="button" onClick={addCopyrightElement} className="w-full rounded-xl border border-teal-200 bg-teal-50 px-3 py-2.5 text-sm font-semibold text-teal-800 transition hover:border-teal-400 hover:bg-teal-100">Copyright als onderdeel toevoegen</button> : null}
        {columns.map((column, columnIndex) => (
          <div key={column.columnId} className="rounded-xl border border-slate-200 p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Kolom {columnIndex + 1}</p>
            <ul className="mt-2 space-y-1">
              {column.elements.map((element) => (
                <li key={element.elementId}>
                  <button
                    type="button"
                    onClick={() => setSelected({ columnId: column.columnId, elementId: element.elementId })}
                    className={cn(
                      'flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm transition',
                      selected?.elementId === element.elementId
                        ? 'bg-teal-50 font-semibold text-teal-800'
                        : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    {FOOTER_ELEMENT_LABELS[element.type]}
                  </button>
                </li>
              ))}
            </ul>
            <AddElementMenu onAdd={(type) => addElement(column.columnId, type)} />
          </div>
        ))}
      </div>

      {selectedElement && selected ? (
        <FooterElementEditor
          element={selectedElement}
          pages={pages}
          columnCount={columns.length}
          onPatch={(patch) => patchElement(selected.columnId, selected.elementId, patch)}
          onDelete={() => removeElement(selected.columnId, selected.elementId)}
          onMove={(delta) => moveElement(selected.columnId, selected.elementId, delta)}
          onMoveToColumn={(index) => {
            const target = columns[index];
            if (target) moveToColumn(selected.columnId, selected.elementId, target.columnId);
          }}
          onOpenMedia={onOpenMedia}
        />
      ) : null}
    </div>
  );
}

function newFooterElement(type: FooterElement['type'], patch: Partial<FooterElement> = {}): FooterElement {
  return {
    elementId: createBlockId(),
    type,
    heading: type === 'links' ? 'Navigatie' : type === 'contact' ? 'Contact' : type === 'socials' ? 'Volg ons' : '',
    text: '',
    links: [],
    socials: [],
    logos: [],
    address: '',
    email: '',
    phone: '',
    buttonLabel: '',
    flow: 'stack',
    gap: 12,
    padding: 0,
    ...patch,
  } as FooterElement;
}

function AddElementMenu({ onAdd }: { onAdd: (type: FooterElement['type']) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
      >
        <Plus className="h-3.5 w-3.5" /> Onderdeel toevoegen
      </button>
      {open ? (
        <div className="mt-2 grid grid-cols-2 gap-1">
          {(Object.keys(FOOTER_ELEMENT_LABELS) as FooterElement['type'][]).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => { onAdd(type); setOpen(false); }}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-left text-xs font-medium text-slate-700 transition hover:border-teal-300 hover:bg-teal-50"
            >
              {FOOTER_ELEMENT_LABELS[type]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FooterElementEditor({
  element,
  pages,
  columnCount,
  onPatch,
  onDelete,
  onMove,
  onMoveToColumn,
  onOpenMedia,
}: {
  element: FooterElement;
  pages: SitePage[];
  columnCount: number;
  onPatch: (patch: Partial<FooterElement>) => void;
  onDelete: () => void;
  onMove: (delta: number) => void;
  onMoveToColumn: (index: number) => void;
  onOpenMedia: (target: MediaTarget) => void;
}) {
  return (
    <div className="space-y-4 rounded-xl border border-teal-200 bg-teal-50/30 p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-900">{FOOTER_ELEMENT_LABELS[element.type]}</h4>
        <div className="flex gap-1">
          <MiniButton label="Omhoog" onClick={() => onMove(-1)}><ArrowUp className="h-3.5 w-3.5" /></MiniButton>
          <MiniButton label="Omlaag" onClick={() => onMove(1)}><ArrowDown className="h-3.5 w-3.5" /></MiniButton>
          <MiniButton label="Verwijderen" destructive onClick={onDelete}><Trash2 className="h-3.5 w-3.5" /></MiniButton>
        </div>
      </div>

      {columnCount > 1 ? (
        <label className="block">
          <span className="block text-xs font-semibold text-slate-700">Verplaatsen naar kolom</span>
          <select onChange={(event) => onMoveToColumn(Number(event.target.value))} value="" className={field}>
            <option value="" disabled>Kies een kolom…</option>
            {Array.from({ length: columnCount }, (_, index) => (
              <option key={index} value={index}>Kolom {index + 1}</option>
            ))}
          </select>
        </label>
      ) : null}

      <Segmented
        label="Uitlijning"
        value={element.align ?? 'left'}
        options={[['left', 'Links'], ['center', 'Midden'], ['right', 'Rechts']]}
        onChange={(value) => onPatch({ align: value as FooterElement['align'] })}
      />
      <label className="block text-xs font-semibold text-slate-700">Tekstgrootte (px)
        <input type="number" min={10} max={96} value={element.fontSize ?? ''} onChange={(event) => onPatch({ fontSize: event.target.value ? Number(event.target.value) : undefined })} placeholder="Site standaard" className={field} />
      </label>

      <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Onderdeel-layout</p>
        <Segmented
          label="Inhoud opmaken"
          value={element.flow ?? 'stack'}
          options={[['stack', 'Onder elkaar'], ['row', 'Naast elkaar'], ['none', 'Vrije inhoud']]}
          onChange={(value) => onPatch({ flow: value as NonNullable<FooterElement['flow']> })}
        />
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs font-semibold text-slate-700">Ruimte (px)
            <input type="number" min={0} max={96} value={element.gap ?? 12} onChange={(event) => onPatch({ gap: Number(event.target.value) || 0 })} className={field} />
          </label>
          <label className="block text-xs font-semibold text-slate-700">Padding (px)
            <input type="number" min={0} max={96} value={element.padding ?? 0} onChange={(event) => onPatch({ padding: Number(event.target.value) || 0 })} className={field} />
          </label>
        </div>
      </div>

      {element.freeformLayout?.placementVersion === 2
        ? <FooterFreeformGeometryEditor element={element} onPatch={onPatch} />
        : <FooterPlacementEditor element={element} onPatch={onPatch} />}

      {element.type !== 'copyright' ? (
        <label className="block">
          <span className="block text-xs font-semibold text-slate-700">{element.type === 'logo' ? 'Naam' : 'Kopje'}</span>
          <input value={element.heading} onChange={(event) => onPatch({ heading: event.target.value })} className={field} />
        </label>
      ) : null}

      {['text', 'logo', 'copyright', 'cookie_preferences'].includes(element.type) ? (
        <label className="block">
          <span className="block text-xs font-semibold text-slate-700">Tekst</span>
          <textarea rows={3} value={element.text} onChange={(event) => onPatch({ text: event.target.value })} className={`${field} resize-y`} />
        </label>
      ) : null}

      {element.type === 'contact' ? (
        <>
          <TextField label="Adres" value={element.address} onChange={(address) => onPatch({ address })} />
          <TextField label="E-mail" value={element.email} onChange={(email) => onPatch({ email })} />
          <TextField label="Telefoon" value={element.phone} onChange={(phone) => onPatch({ phone })} />
        </>
      ) : null}

      {element.type === 'button' ? (
        <>
          <TextField label="Knoptekst" value={element.buttonLabel} onChange={(buttonLabel) => onPatch({ buttonLabel })} />
          <label className="block">
            <span className="block text-xs font-semibold text-slate-700">Gaat naar</span>
            <select
              value={element.destination?.pageId ?? ''}
              onChange={(event) => onPatch({ destination: { kind: 'page', pageId: event.target.value || null } })}
              className={field}
            >
              <option value="">Kies een pagina…</option>
              {pages.map((page) => <option key={page.pageId} value={page.pageId}>{page.title || 'Naamloze pagina'}</option>)}
            </select>
          </label>
        </>
      ) : null}

      {element.type === 'links' || element.type === 'legal' ? (
        <LinkListEditor links={element.links} pages={pages} onChange={(links) => onPatch({ links })} />
      ) : null}

      {element.type === 'socials' ? (
        <SocialEditor socials={element.socials} onChange={(socials) => onPatch({ socials })} />
      ) : null}

      {element.type === 'partner_logos' ? (
        <PartnerLogoEditor elementId={element.elementId} logos={element.logos} onChange={(logos) => onPatch({ logos })} onOpenMedia={onOpenMedia} />
      ) : null}
    </div>
  );
}

function FooterFreeformGeometryEditor({
  element,
  onPatch,
}: {
  element: FooterElement;
  onPatch: (patch: Partial<FooterElement>) => void;
}) {
  const [viewport, setViewport] = useState<ViewportKey>('desktop');
  const layout = element.freeformLayout!;
  const current = geometryForViewport(layout, viewport) ?? layout.desktop;
  const patchGeometry = (patch: Partial<FreeformGeometry>) => {
    const next = { ...current, ...patch };
    onPatch({
      freeformLayout: {
        ...layout,
        placementVersion: 2,
        ...(viewport === 'desktop' ? { desktop: next } : { [viewport]: next }),
      },
    });
  };

  return (
    <div className="space-y-3 rounded-xl border border-teal-200 bg-teal-50/30 p-3">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Vrije positie</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">X, Y en breedte zijn de v2-geometrie in logische pixels. Het oude raster wijzigt dit element niet.</p>
      </div>
      <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
        {(['desktop', 'tablet', 'mobile'] as ViewportKey[]).map((key) => (
          <button key={key} type="button" onClick={() => setViewport(key)} className={cn('rounded-md px-2 py-1.5 text-xs font-semibold', viewport === key ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500')}>
            {key === 'desktop' ? 'Desktop' : key === 'tablet' ? 'Tablet' : 'Mobiel'}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <PlacementNumber label="X" value={current.x} min={0} max={10000} onChange={(value) => patchGeometry({ x: value })} />
        <PlacementNumber label="Y" value={current.y} min={0} max={10000} onChange={(value) => patchGeometry({ y: value })} />
        <PlacementNumber label="Breedte" value={current.width} min={40} max={4000} onChange={(value) => patchGeometry({ width: value })} />
        {current.heightMode === 'fixed' ? <PlacementNumber label="Hoogte" value={current.height ?? 96} min={40} max={4000} onChange={(value) => patchGeometry({ height: value })} /> : null}
      </div>
      <Segmented
        label="Hoogte"
        value={current.heightMode}
        options={[['auto', 'Automatisch'], ['fixed', 'Vast']]}
        onChange={(value) => patchGeometry(value === 'auto' ? { heightMode: 'auto', height: null } : { heightMode: 'fixed', height: current.height ?? 96 })}
      />
      <Segmented
        label="Laag"
        value={current.layer}
        options={[['background', 'Achtergrond'], ['normal', 'Normaal'], ['foreground', 'Voorgrond']]}
        onChange={(value) => patchGeometry({ layer: value as FreeformGeometry['layer'] })}
      />
    </div>
  );
}

function FooterPlacementEditor({
  element,
  onPatch,
}: {
  element: FooterElement;
  onPatch: (patch: Partial<FooterElement>) => void;
}) {
  const [viewport, setViewport] = useState<ViewportKey>('desktop');
  const desktop = element.layout?.desktop ?? { gridStart: 1, gridSpan: 12, offsetX: 0, offsetY: 0, layer: 'normal' as const };
  const current = viewport === 'desktop' ? desktop : { ...desktop, ...(element.layout?.[viewport] ?? {}) };
  const patchPlacement = (patch: Partial<typeof desktop>) => {
    const layout = element.layout ?? { desktop };
    if (viewport === 'desktop') {
      onPatch({ layout: { ...layout, desktop: { ...desktop, ...patch } } as FooterElement['layout'] });
      return;
    }
    onPatch({ layout: { ...layout, desktop, [viewport]: { ...(element.layout?.[viewport] ?? {}), ...patch } } as FooterElement['layout'] });
  };

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Rasterpositie</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">Sleep of wijzig de positie per schermformaat. De canvas toont de wijziging direct.</p>
      </div>
      <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
        {(['desktop', 'tablet', 'mobile'] as ViewportKey[]).map((key) => (
          <button key={key} type="button" onClick={() => setViewport(key)} className={cn('rounded-md px-2 py-1.5 text-xs font-semibold', viewport === key ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500')}>
            {key === 'desktop' ? 'Desktop' : key === 'tablet' ? 'Tablet' : 'Mobiel'}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <PlacementNumber label="Kolom start" value={current.gridStart} min={1} max={12} onChange={(value) => patchPlacement({ gridStart: value })} />
        <PlacementNumber label="Breedte" value={current.gridSpan} min={1} max={12} onChange={(value) => patchPlacement({ gridSpan: value })} />
        <PlacementNumber label="X-offset" value={current.offsetX} min={-1200} max={1200} onChange={(value) => patchPlacement({ offsetX: value })} />
        <PlacementNumber label="Y-offset" value={current.offsetY} min={-1200} max={1200} onChange={(value) => patchPlacement({ offsetY: value })} />
        <PlacementNumber label="Min. hoogte" value={current.height ?? 160} min={40} max={1200} onChange={(value) => patchPlacement({ height: value })} />
      </div>
    </div>
  );
}

function PlacementNumber({ label, value, min, max, onChange }: { label: string; value: number; min: number; max: number; onChange: (value: number) => void }) {
  return (
    <label className="block text-xs font-semibold text-slate-700">{label}
      <input type="number" min={min} max={max} step={1} value={value} onChange={(event) => onChange(Math.max(min, Math.min(max, Number(event.target.value) || 0)))} className={field} />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Repeating sub-editors
// ---------------------------------------------------------------------------

function LinkListEditor({
  links,
  pages,
  onChange,
}: {
  links: FooterElement['links'];
  pages: SitePage[];
  onChange: (links: FooterElement['links']) => void;
}) {
  return (
    <div className="space-y-2">
      <span className="block text-xs font-semibold text-slate-700">Links</span>
      {links.length === 0 ? (
        <p className="rounded-lg bg-white px-3 py-2 text-xs text-slate-500">
          Zonder links toont de footer automatisch je hoofdnavigatie.
        </p>
      ) : null}
      {links.map((link, index) => (
        <div key={index} className="flex gap-1.5">
          <input
            value={link.label}
            onChange={(event) => onChange(links.map((item, i) => i === index ? { ...item, label: event.target.value } : item))}
            placeholder="Tekst"
            className={`${field} mt-0 flex-1`}
          />
          <select
            value={link.pageId ?? ''}
            onChange={(event) => onChange(links.map((item, i) => i === index ? { ...item, pageId: event.target.value || null } : item))}
            className={`${field} mt-0 flex-1`}
          >
            <option value="">Pagina…</option>
            {pages.map((page) => <option key={page.pageId} value={page.pageId}>{page.title || 'Naamloos'}</option>)}
          </select>
          <MiniButton label="Verwijderen" destructive onClick={() => onChange(links.filter((_, i) => i !== index))}>
            <Trash2 className="h-3.5 w-3.5" />
          </MiniButton>
        </div>
      ))}
      <AddRow label="Link toevoegen" onClick={() => onChange([...links, { label: '', pageId: null, href: null }])} />
    </div>
  );
}

function SocialEditor({
  socials,
  onChange,
}: {
  socials: FooterElement['socials'];
  onChange: (socials: FooterElement['socials']) => void;
}) {
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= socials.length) return;
    const next = socials.slice();
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <span className="block text-xs font-semibold text-slate-700">Kanalen</span>
      {socials.map((social, index) => (
        <div key={index} className="flex items-center gap-1.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-slate-600 ring-1 ring-slate-200">
            <SocialMark platform={social.platform} />
          </span>
          <select
            value={social.platform}
            onChange={(event) => onChange(socials.map((item, i) => i === index ? { ...item, platform: event.target.value } : item))}
            className={`${field} mt-0 w-28`}
          >
            {SOCIAL_PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>{platform}</option>
            ))}
          </select>
          <input
            value={social.href}
            onChange={(event) => onChange(socials.map((item, i) => i === index ? { ...item, href: event.target.value } : item))}
            placeholder="https://…"
            className={`${field} mt-0 flex-1`}
          />
          <MiniButton label="Omhoog" onClick={() => move(index, -1)}><ArrowUp className="h-3.5 w-3.5" /></MiniButton>
          <MiniButton label="Verwijderen" destructive onClick={() => onChange(socials.filter((_, i) => i !== index))}>
            <Trash2 className="h-3.5 w-3.5" />
          </MiniButton>
        </div>
      ))}
      <AddRow
        label="Kanaal toevoegen"
        onClick={() => onChange([...socials, { platform: SOCIAL_PLATFORMS[0]!, href: '' }])}
      />
      <p className="text-xs text-slate-500">Alleen veilige https-adressen worden op de website als link getoond.</p>
    </div>
  );
}

function PartnerLogoEditor({
  elementId,
  logos,
  onChange,
  onOpenMedia,
}: {
  elementId: string;
  logos: FooterElement['logos'];
  onChange: (logos: FooterElement['logos']) => void;
  onOpenMedia: (target: MediaTarget) => void;
}) {
  return (
    <div className="space-y-2">
      <span className="block text-xs font-semibold text-slate-700">Logo&apos;s</span>
      {logos.map((logo, index) => (
        <div key={index} className="flex gap-1.5">
          <input
            value={logo.name}
            onChange={(event) => onChange(logos.map((item, i) => i === index ? { ...item, name: event.target.value } : item))}
            placeholder="Naam"
            className={`${field} mt-0 flex-1`}
          />
          <input
            value={logo.assetId ?? ''}
            onChange={(event) => onChange(logos.map((item, i) => i === index ? { ...item, assetId: event.target.value || null } : item))}
            placeholder="Media-id"
            className={`${field} mt-0 w-28`}
          />
          <button type="button" onClick={() => onOpenMedia({ kind: 'footer-logo', elementId, index })} className="rounded-xl border border-dashed border-teal-300 bg-teal-50 px-2 text-[11px] font-semibold text-teal-800 hover:bg-teal-100">Kiezen</button>
          <MiniButton label="Verwijderen" destructive onClick={() => onChange(logos.filter((_, i) => i !== index))}>
            <Trash2 className="h-3.5 w-3.5" />
          </MiniButton>
        </div>
      ))}
      <AddRow label="Logo toevoegen" onClick={() => onChange([...logos, { name: '', assetId: null, href: null }])} />
      <p className="text-xs text-slate-500">
        Zonder afbeelding toont de website de naam. De mediabibliotheek volgt later.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared controls
// ---------------------------------------------------------------------------

export function Intro({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-slate-900">{title}</h3>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">{description}</p>
    </div>
  );
}

export function Note({ children }: { children: React.ReactNode }) {
  return <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">{children}</p>;
}

export function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <span className="block text-sm font-semibold text-slate-800">{label}</span>
      <div role="radiogroup" aria-label={label} className="mt-1.5 flex flex-wrap gap-1.5">
        {options.map(([optionValue, optionLabel]) => (
          <button
            key={optionValue}
            type="button"
            role="radio"
            aria-checked={value === optionValue}
            onClick={() => onChange(optionValue)}
            className={cn(
              'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition',
              value === optionValue
                ? 'border-teal-400 bg-teal-50 text-teal-800'
                : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50',
            )}
          >
            {optionLabel}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PickerGroup({
  label,
  value,
  options,
  onChange,
  columns = 1,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string; preview: React.ReactNode }>;
  onChange: (value: string) => void;
  columns?: number;
}) {
  return (
    <div>
      <span className="block text-sm font-semibold text-slate-800">{label}</span>
      <div role="radiogroup" aria-label={label} className={cn('mt-2 grid gap-2', columns === 4 ? 'grid-cols-4' : 'grid-cols-1')}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            aria-label={option.label}
            title={option.label}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-xl border p-2 text-left transition',
              value === option.value
                ? 'border-teal-400 bg-teal-50/60 ring-1 ring-teal-300'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
            )}
          >
            {option.preview}
            <span className="mt-1.5 block truncate text-[11px] font-medium text-slate-600">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-teal-600"
      />
      {label}
    </label>
  );
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700">{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className={field} />
    </label>
  );
}

function ColorField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-800">{label}</span>
      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
        <input type="color" value={value || '#ffffff'} onChange={(event) => onChange(event.target.value)} aria-label={`${label} kiezen`} className="h-9 w-10 cursor-pointer rounded-lg border border-slate-300 bg-white p-1" />
        <span className="text-sm font-semibold text-slate-700">{value ? 'Aangepaste kleur' : 'Themakleur'}</span>
      </div>
      <details className="mt-2 rounded-lg border border-slate-200 px-3 py-2">
        <summary className="cursor-pointer text-xs font-semibold text-slate-600">Precieze kleur invoeren</summary>
        <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder || '#RRGGBB'} pattern="^#[0-9a-fA-F]{6}$" className={cn(field, 'mt-2')} />
      </details>
    </label>
  );
}

function MiniButton({
  label,
  onClick,
  destructive,
  children,
}: {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition',
        destructive ? 'hover:border-red-200 hover:bg-red-50 hover:text-red-600' : 'hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700',
      )}
    >
      {children}
    </button>
  );
}

function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
    >
      <Plus className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

export { Copy };
